import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { MagnifyingGlass, Phone, DeviceMobile, Buildings, MapPin, IdentificationCard, ArrowsClockwise } from "@phosphor-icons/react";

const toE164 = (s) => {
  const d = (s || "").replace(/[^\d+]/g, "");
  return d.startsWith("+") ? d : "+" + d.replace(/\D/g, "");
};

export default function Lookup() {
  const [num, setNum] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const qc = useQueryClient();
  const [histQuery, setHistQuery] = useState("");
  const { data: hist } = useQuery({ queryKey: ["lookup-history"], queryFn: async () => (await api.get("/lookup/history")).data });
  const historyRows = (() => {
    const seen = new Set();
    return (hist?.history || [])
      .filter((h) => !histQuery || (h.number || "").includes(histQuery))
      .filter((h) => { if (seen.has(h.number)) return false; seen.add(h.number); return true; });
  })();

  const run = async (override) => {
    const n = toE164(override ?? num);
    if (n.length < 8) { toast.error("Enter a valid number in international format"); return; }
    setNum(n);
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get("/lookup", { params: { number: n } });
      setResult(data);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["lookup-history"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const rows = result ? [
    { icon: Phone, label: "Number", value: result.national_format || result.phone_number },
    { icon: DeviceMobile, label: "Line type", value: result.line_type ? result.line_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—" },
    { icon: Buildings, label: "Carrier", value: result.carrier || "—" },
    { icon: IdentificationCard, label: "Caller name", value: result.caller_name || "—" },
    { icon: MapPin, label: "Location", value: [result.city, result.state, result.country_code].filter(Boolean).join(", ") || "—" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="lookup-page">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-primary"><MagnifyingGlass size={26} weight="duotone" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Number Lookup</h1>
          <p className="mt-1 text-muted-foreground">Identify carrier, line type and caller name for any number.</p>
        </div>
      </div>

      <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input data-testid="lookup-input" value={num} onChange={(e) => setNum(e.target.value)} placeholder="+1 312 945 7420"
            onKeyDown={(e) => e.key === "Enter" && run()}
            className="w-full rounded-xl border border-input bg-background px-4 py-3 font-mono-metric text-lg outline-none focus:border-primary" />
          <button data-testid="lookup-btn" onClick={() => run()} disabled={busy}
            className="flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
            <MagnifyingGlass size={18} weight="bold" /> {busy ? "Looking…" : "Look up"}
          </button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">A small wallet fee applies to each lookup.</p>

        {result && (
          <>
          <div data-testid="lookup-result" className="mt-5 divide-y divide-border rounded-xl border border-border">
            {rows.map((r) => (
              <div key={r.label} className="flex items-center gap-3 px-4 py-3">
                <r.icon size={18} className="shrink-0 text-primary" />
                <span className="text-sm text-muted-foreground">{r.label}</span>
                <span className="ml-auto text-right text-sm font-medium">{r.value}</span>
              </div>
            ))}
            {result.ported != null && (
              <div className="flex items-center gap-3 px-4 py-3">
                <IdentificationCard size={18} className="shrink-0 text-primary" />
                <span className="text-sm text-muted-foreground">Ported</span>
                <span className="ml-auto text-sm font-medium">{result.ported ? "Yes" : "No"}</span>
              </div>
            )}
          </div>
          {result.fee_cents > 0 && (
            <p data-testid="lookup-fee" className="mt-3 text-center text-xs text-muted-foreground">Charged {money(result.fee_cents, "USD")} to your wallet</p>
          )}
          </>
        )}
      </div>

      {historyRows.length > 0 && (
        <div className="mx-auto max-w-xl" data-testid="lookup-history">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent lookups</h2>
            <input data-testid="lookup-history-search" value={histQuery} onChange={(e) => setHistQuery(e.target.value)} placeholder="Search…"
              className="w-32 rounded-lg border border-input bg-background px-2 py-1 text-xs outline-none focus:border-primary" />
          </div>
          <div className="space-y-2">
            {historyRows.map((h) => (
              <button key={h.id} data-testid={`history-${h.id}`} onClick={() => run(h.number)}
                className="flex w-full items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:border-primary">
                <div className="min-w-0">
                  <p className="font-mono-metric text-sm font-medium">{h.number}</p>
                  <p className="truncate text-xs text-muted-foreground">{[h.result?.carrier, h.result?.line_type].filter(Boolean).join(" · ") || "—"}{h.created_at ? ` · ${shortDate(h.created_at)}` : ""}</p>
                </div>
                <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-primary"><ArrowsClockwise size={14} /> Re-lookup</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
